import { EmptyState } from "@/components/common/EmptyState/EmptyState"
import { Typography } from "@/components/common/Typography/Typography"
import { Button } from "@/components/ui/button"
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip"
import {
  useTranslation,
  type TranslationKeys,
} from "@/hooks/useTranslation/useTranslation"
import type { DataRoomFile, DataRoomFileStatus } from "@/types/dataRoom"
import { classNames } from "@/utils/classNames"
import { getFileExtension } from "@/utils/fileUtils"
import {
  IconCheck,
  IconDownload,
  IconFile,
  IconFileTypeCsv,
  IconFileTypeDoc,
  IconFileTypeDocx,
  IconFileTypePdf,
  IconFileTypePpt,
  IconFileTypeXls,
  IconFileZip,
  IconLoader2,
  IconPhoto,
  IconRefresh,
  IconTrash,
  IconX,
} from "@tabler/icons-react"
import { AnimatePresence } from "motion/react"
import * as m from "motion/react-m"
import { useMemo } from "react"
import { DataRoomNoteEditor } from "./DataRoomNoteEditor"
import type { DataRoomUploadEntry } from "./useDataRoomUpload"

const FILE_ICON_BY_EXTENSION: Record<string, React.ElementType> = {
  pdf: IconFileTypePdf,
  doc: IconFileTypeDoc,
  docx: IconFileTypeDocx,
  xls: IconFileTypeXls,
  xlsx: IconFileTypeXls,
  xlsm: IconFileTypeXls,
  csv: IconFileTypeCsv,
  ppt: IconFileTypePpt,
  pptx: IconFileTypePpt,
  zip: IconFileZip,
  png: IconPhoto,
  jpg: IconPhoto,
  jpeg: IconPhoto,
}

export function dataRoomFileIcon(fileName: string): React.ElementType {
  return FILE_ICON_BY_EXTENSION[getFileExtension(fileName)] ?? IconFile
}

/** No shared byte formatter exists in `utils/`; this stays the single copy. */
export function formatFileSize(bytes: number): string {
  if (bytes <= 0) return "0 KB"
  const units = ["B", "KB", "MB", "GB"]
  const exponent = Math.min(
    Math.floor(Math.log(bytes) / Math.log(1024)),
    units.length - 1,
  )
  const value = bytes / 1024 ** exponent
  return `${exponent === 0 ? value : value.toFixed(1)} ${units[exponent]}`
}

const STATUS_LABEL_KEYS: Record<DataRoomFileStatus, TranslationKeys> = {
  uploaded: "DATA_ROOM/STATUS_UPLOADED",
  in_review: "DATA_ROOM/STATUS_IN_REVIEW",
  accepted: "DATA_ROOM/STATUS_ACCEPTED",
  changes_requested: "DATA_ROOM/STATUS_CHANGES_REQUESTED",
}

// Status is a dot and a word, never a filled block. The palette has no blue
// token, so "in review" uses the neutral white treatment.
const STATUS_DOT_CLASS: Record<DataRoomFileStatus, string> = {
  uploaded: "bg-grey5",
  in_review: "bg-white",
  accepted: "bg-success",
  changes_requested: "bg-warning",
}

const STATUS_TEXT_CLASS: Record<DataRoomFileStatus, string> = {
  uploaded: "text-grey5",
  in_review: "text-white",
  accepted: "text-success",
  changes_requested: "text-warning",
}

interface DataRoomStatusProps {
  status: DataRoomFileStatus
}

export const DataRoomStatus: React.FC<DataRoomStatusProps> = ({ status }) => {
  const { translate } = useTranslation()
  return (
    <span
      className={classNames(
        "inline-flex shrink-0 items-center gap-2 whitespace-nowrap transition-colors duration-200",
        STATUS_TEXT_CLASS[status],
      )}
      data-testid={`data-room-status-${status}`}
    >
      <span
        aria-hidden="true"
        className={classNames(
          "h-1.5 w-1.5 rounded-full",
          STATUS_DOT_CLASS[status],
        )}
      />
      <Typography as="span" variant="textXS" className="text-inherit!">
        {translate(STATUS_LABEL_KEYS[status])}
      </Typography>
    </span>
  )
}

const MetaLine: React.FC<{ parts: Array<string | undefined | null> }> = ({
  parts,
}) => (
  <Typography variant="textXS" className="truncate text-grey5">
    {parts.filter(Boolean).join(" · ")}
  </Typography>
)

interface SavedNoteProps {
  file: DataRoomFile
  editable: boolean
  onEdit: () => void
}

/** The customer's saved note under a row. Editable notes are a quiet
 * button that reopens the editor; once turnus accepted the file the note
 * turns into plain text and says so, instead of posing as a disabled
 * control. */
const SavedNote: React.FC<SavedNoteProps> = ({ file, editable, onEdit }) => {
  const { translate } = useTranslation()
  const body = (
    <>
      <Typography variant="textXS" className="text-grey5">
        {translate("DATA_ROOM/NOTE_LABEL")}
      </Typography>
      <Typography
        variant="textS"
        className="mt-0.5 whitespace-pre-wrap wrap-break-word text-grey6"
      >
        {file.customerNote}
      </Typography>
    </>
  )
  if (editable) {
    return (
      <button
        type="button"
        onClick={onEdit}
        aria-label={translate("DATA_ROOM/NOTE_ARIA", { name: file.fileName })}
        data-testid="data-room-note"
        className="mb-3.5 ml-9 block max-w-2xl rounded-sm text-left transition-opacity hover:opacity-80 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring"
      >
        {body}
      </button>
    )
  }
  return (
    <div className="mb-3.5 ml-9 max-w-2xl" data-testid="data-room-note">
      {body}
      <Typography variant="textXS" className="mt-1 text-grey5">
        {translate("DATA_ROOM/NOTE_LOCKED")}
      </Typography>
    </div>
  )
}

export interface DataRoomFileListProps {
  files: DataRoomFile[]
  uploads: DataRoomUploadEntry[]
  isLoading: boolean
  isError: boolean
  onRetry: () => void
  onPreviewFile: (file: DataRoomFile) => void
  onDownloadFile: (file: DataRoomFile) => void
  onDeleteFile: (file: DataRoomFile) => void
  onRetryUpload: (entryId: string) => void
  onDismissUpload: (entryId: string) => void
  /** Maps an upload entry's error code to translated copy. */
  uploadErrorLabel: (entry: DataRoomUploadEntry) => string
  /** BCP 47 locale for dates, derived from the UI language. */
  locale: string
  /** Folder names by id; when given, rows carry their folder as context
   * (used in the root view where files from every folder can appear). */
  folderNameById?: Map<string, string>
  /** The folder currently in view (null = root). `uploads` is ALREADY
   * scoped to this folder by the caller — this is used only to key the
   * uploads list's `AnimatePresence`, so switching folders remounts it and
   * drops out-of-view rows instantly instead of playing their exit fade
   * (that fade is for a row genuinely finishing/being dismissed, not for
   * navigating away from it). */
  currentFolderId: string | null
  /** Id of the file whose note editor is open, if any — only one row edits
   * at a time. */
  editingNoteFileId: string | null
  onOpenNoteEditor: (file: DataRoomFile) => void
  onCloseNoteEditor: () => void
  onSaveNote: (file: DataRoomFile, note: string) => void
  /** True while a note save is in flight; disables the editor's Save button. */
  isSavingNote: boolean
  /** Unsaved note text per file id, kept by the page across navigation. */
  noteDrafts: ReadonlyMap<string, string>
  onNoteDraftChange: (fileId: string, value: string) => void
  /** "Überarbeitete Datei hochladen" under a reviewer's request. */
  onUploadRevision: (file: DataRoomFile) => void
  /** Secondary lists (templates, files sent back) render without the
   * uploads strip and under their own test id, so the page can show several
   * lists at once. */
  variant?: "primary" | "secondary"
}

/** Rows of files (and in-flight uploads) for the folder in view, separated
 * by hairlines only. Renders nothing when there is nothing to show. */
export const DataRoomFileList: React.FC<DataRoomFileListProps> = ({
  files,
  uploads,
  isLoading,
  isError,
  onRetry,
  onPreviewFile,
  onDownloadFile,
  onDeleteFile,
  onRetryUpload,
  onDismissUpload,
  uploadErrorLabel,
  locale,
  folderNameById,
  currentFolderId,
  editingNoteFileId,
  onOpenNoteEditor,
  onCloseNoteEditor,
  onSaveNote,
  isSavingNote,
  noteDrafts,
  onNoteDraftChange,
  onUploadRevision,
  variant = "primary",
}) => {
  const { translate } = useTranslation()

  const dateFormatter = useMemo(
    () =>
      new Intl.DateTimeFormat(locale, {
        day: "numeric",
        month: "short",
        year: "numeric",
      }),
    [locale],
  )

  // A finished upload lingers as its own row for a moment (check mark, full
  // bar). Its persisted twin stays hidden until that row leaves, so the file
  // never appears twice.
  const lingeringServerIds = useMemo(
    () =>
      new Set(
        uploads
          .filter((u) => u.status === "uploaded" && u.serverFileId)
          .map((u) => u.serverFileId as string),
      ),
    [uploads],
  )

  const sortedFiles = useMemo(
    () =>
      files
        .filter((file) => !lingeringServerIds.has(file.id))
        .sort((a, b) => {
          if (a.isTurnusUpload !== b.isTurnusUpload) {
            return a.isTurnusUpload ? -1 : 1
          }
          // A file turnus sent back needs the customer's hand; it must not
          // sink under newer uploads.
          const aNeedsWork = a.status === "changes_requested"
          const bNeedsWork = b.status === "changes_requested"
          if (aNeedsWork !== bNeedsWork) return aNeedsWork ? -1 : 1
          return b.createdAt.getTime() - a.createdAt.getTime()
        }),
    [files, lingeringServerIds],
  )

  if (isLoading) {
    return (
      <div data-testid="data-room-file-list-skeleton">
        {[0, 1, 2].map((i) => (
          <div
            key={i}
            className="flex items-center gap-4 border-b border-grey4/60 py-3.5"
          >
            <div className="h-5 w-5 animate-pulse rounded bg-grey4/60" />
            <div className="flex flex-1 flex-col gap-2">
              <div className="h-3 w-1/3 animate-pulse rounded bg-grey4/60" />
              <div className="h-2.5 w-1/4 animate-pulse rounded bg-grey4/40" />
            </div>
          </div>
        ))}
      </div>
    )
  }

  if (isError) {
    return (
      <EmptyState
        title={translate("DATA_ROOM/TABLE_ERROR_TITLE")}
        description={translate("DATA_ROOM/TABLE_ERROR_DESCRIPTION")}
        primaryAction={{
          label: translate("DATA_ROOM/TABLE_ERROR_RETRY"),
          onClick: onRetry,
          variant: "outline",
        }}
      />
    )
  }

  if (sortedFiles.length === 0 && uploads.length === 0) return null

  return (
    <div
      data-testid={
        variant === "primary"
          ? "data-room-file-list"
          : "data-room-file-list-secondary"
      }
    >
      {/* Always rendered, even with zero entries: `AnimatePresence` needs to
          stay mounted across the render where the last upload leaves
          `uploads` for its exit fade to run at all. Gating this whole block
          on `uploads.length > 0` (as before) would unmount `AnimatePresence`
          in the very same render that removes the last entry — the single-
          file-upload case — killing the exit animation before it can play. */}
      {variant === "primary" && (
        <ul
          role="list"
          aria-label={translate("DATA_ROOM/UPLOADS_LIST_ARIA")}
          data-testid="data-room-upload-entries"
        >
          <AnimatePresence initial={false} key={currentFolderId ?? "root"}>
            {uploads.map((entry) => {
              const Icon = dataRoomFileIcon(entry.fileName)
              const folderName = entry.folderId
                ? folderNameById?.get(entry.folderId)
                : undefined
              return (
                <m.li
                  key={entry.id}
                  layout="position"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.2, ease: "easeOut" }}
                  className="relative flex items-center gap-4 overflow-hidden border-b border-grey4/60 py-3.5"
                >
                  <Icon
                    size={20}
                    className="shrink-0 text-grey5"
                    aria-hidden="true"
                  />
                  <div className="min-w-0 flex-1">
                    <Typography variant="textS" className="truncate">
                      {entry.fileName}
                    </Typography>
                    {entry.status === "uploading" && (
                      <MetaLine
                        parts={
                          // Bytes are all sent at 100 %; what remains is the
                          // server storing them. Say so instead of sitting on
                          // "100 %" with a spinner.
                          entry.progress >= 100
                            ? [
                                translate("DATA_ROOM/UPLOAD_PROCESSING"),
                                folderName,
                              ]
                            : [
                                translate("DATA_ROOM/UPLOADING_ARIA"),
                                translate("DATA_ROOM/UPLOAD_PROGRESS", {
                                  percent: entry.progress,
                                }),
                                folderName,
                              ]
                        }
                      />
                    )}
                    {entry.status !== "uploading" && (
                      <MetaLine
                        parts={[formatFileSize(entry.file.size), folderName]}
                      />
                    )}
                    {entry.status === "failed" && (
                      <div role="alert" aria-live="polite" className="mt-1">
                        <Typography variant="textXS" className="text-red1">
                          {uploadErrorLabel(entry)}
                        </Typography>
                      </div>
                    )}
                  </div>

                  {entry.status === "uploading" && (
                    <IconLoader2
                      size={16}
                      className="shrink-0 animate-spin text-grey5"
                      aria-label={translate("DATA_ROOM/UPLOADING_ARIA")}
                    />
                  )}
                  {entry.status === "uploaded" && (
                    <div className="flex shrink-0 items-center gap-1.5 text-success">
                      <m.span
                        initial={{ opacity: 0, scale: 0.8 }}
                        animate={{ opacity: 1, scale: 1 }}
                        className="flex items-center"
                      >
                        <IconCheck
                          size={16}
                          aria-hidden="true"
                          data-testid="data-room-upload-check"
                        />
                      </m.span>
                      <Typography
                        as="span"
                        variant="textXS"
                        className="text-inherit!"
                      >
                        {translate("DATA_ROOM/UPLOAD_DONE")}
                      </Typography>
                    </div>
                  )}
                  {entry.status === "failed" && (
                    <div className="flex shrink-0 items-center gap-1">
                      {entry.retryable && (
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-10 w-10 text-grey5 hover:text-white sm:h-8 sm:w-8"
                          aria-label={translate("DATA_ROOM/RETRY_UPLOAD_ARIA", {
                            name: entry.fileName,
                          })}
                          onClick={() => onRetryUpload(entry.id)}
                        >
                          <IconRefresh size={16} />
                        </Button>
                      )}
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-10 w-10 text-grey5 hover:text-white sm:h-8 sm:w-8"
                        aria-label={translate("DATA_ROOM/DISMISS_UPLOAD_ARIA", {
                          name: entry.fileName,
                        })}
                        onClick={() => onDismissUpload(entry.id)}
                      >
                        <IconX size={16} />
                      </Button>
                    </div>
                  )}

                  <div
                    className="absolute bottom-0 left-0 h-px bg-yellow1 transition-[width] duration-150 ease-out"
                    style={{
                      width: `${entry.status === "failed" ? 0 : entry.progress}%`,
                    }}
                    data-testid="data-room-upload-progress-bar"
                    aria-hidden="true"
                  />
                </m.li>
              )
            })}
          </AnimatePresence>
        </ul>
      )}

      {sortedFiles.length > 0 && (
        <ul role="list" aria-label={translate("DATA_ROOM/FILE_LIST_ARIA")}>
          {sortedFiles.map((file, index) => {
            const Icon = dataRoomFileIcon(file.fileName)
            const canDelete = !file.isTurnusUpload && file.status !== "accepted"
            // Same eligibility as delete: turnus's own uploads and files
            // already accepted are locked, notes included.
            const canEditNote = canDelete
            const isEditingNote = editingNoteFileId === file.id
            const folderName = file.folderId
              ? folderNameById?.get(file.folderId)
              : undefined
            const uploader = file.isTurnusUpload
              ? translate("DATA_ROOM/UPLOADED_BY_TURNUS")
              : (file.userName ?? undefined)

            return (
              <m.li
                key={file.id}
                layout="position"
                initial={{ opacity: 0, y: 3 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{
                  duration: 0.15,
                  ease: "easeOut",
                  delay: Math.min(index, 6) * 0.02,
                }}
                className="group border-b border-grey4/60"
              >
                <div
                  // Mouse-only convenience: clicking anywhere in the row opens
                  // the file. This carries no role/tabIndex, so it is not
                  // exposed as a control to assistive tech — keyboard and
                  // screen-reader users open the file through the real
                  // "<name> öffnen" button below, and the download/note/
                  // delete buttons stay independently operable rather than
                  // being nested inside an ARIA button.
                  onClick={() => onPreviewFile(file)}
                  className={classNames(
                    "-mx-3 flex cursor-pointer flex-wrap items-center gap-x-4 gap-y-2 rounded-lg px-3 py-3.5 transition-colors duration-150 sm:flex-nowrap",
                    "hover:bg-grey2",
                  )}
                >
                  <Icon
                    size={20}
                    className={classNames(
                      "shrink-0",
                      file.isTurnusUpload ? "text-yellow1" : "text-grey5",
                    )}
                    aria-hidden="true"
                  />

                  <div className="min-w-0 flex-1 basis-full sm:basis-auto">
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <button
                            type="button"
                            onClick={(event) => {
                              event.stopPropagation()
                              onPreviewFile(file)
                            }}
                            aria-label={translate("DATA_ROOM/OPEN_FILE_ARIA", {
                              name: file.fileName,
                            })}
                            className="block min-w-0 rounded-sm text-left focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring"
                          >
                            <Typography
                              variant="textS"
                              className="line-clamp-2 wrap-break-word sm:truncate"
                            >
                              {file.fileName}
                            </Typography>
                          </button>
                        </TooltipTrigger>
                        <TooltipContent side="bottom">
                          {file.fileName}
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                    <MetaLine
                      parts={[
                        uploader,
                        dateFormatter.format(file.createdAt),
                        formatFileSize(file.fileSizeBytes),
                        folderName,
                      ]}
                    />
                    {file.isTurnusUpload && file.templateDescription && (
                      <span
                        className="mt-1 block max-w-2xl"
                        data-testid="data-room-template-description"
                      >
                        <Typography
                          variant="textXS"
                          className="line-clamp-2 text-grey6"
                        >
                          {file.templateDescription}
                        </Typography>
                      </span>
                    )}
                    {canEditNote && !file.customerNote && !isEditingNote && (
                      <button
                        type="button"
                        onClick={(event) => {
                          event.stopPropagation()
                          onOpenNoteEditor(file)
                        }}
                        aria-label={translate("DATA_ROOM/NOTE_ARIA", {
                          name: file.fileName,
                        })}
                        data-testid="data-room-note-add"
                        className="mt-1 block rounded-sm text-left text-grey5 transition-colors hover:text-white focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring"
                      >
                        <Typography
                          as="span"
                          variant="textXS"
                          className="text-inherit!"
                        >
                          + {translate("DATA_ROOM/NOTE_ADD")}
                        </Typography>
                      </button>
                    )}
                  </div>

                  {file.isTurnusUpload ? (
                    <div className="flex shrink-0 items-center gap-3">
                      <span
                        className="whitespace-nowrap text-yellow1"
                        data-testid="data-room-template-tag"
                      >
                        <Typography
                          as="span"
                          variant="textXS"
                          className="text-inherit!"
                        >
                          {translate("DATA_ROOM/TEMPLATE_TAG")}
                        </Typography>
                      </span>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-10 gap-1.5 text-grey5 hover:text-white sm:h-8"
                        aria-label={translate("DATA_ROOM/DOWNLOAD_ARIA", {
                          name: file.fileName,
                        })}
                        onClick={(event) => {
                          event.stopPropagation()
                          onDownloadFile(file)
                        }}
                        data-testid="data-room-template-download"
                      >
                        <IconDownload size={16} aria-hidden="true" />
                        {translate("DATA_ROOM/TEMPLATE_DOWNLOAD")}
                      </Button>
                    </div>
                  ) : (
                    <DataRoomStatus status={file.status} />
                  )}

                  {!file.isTurnusUpload && (
                    <div
                      className={classNames(
                        "flex shrink-0 items-center gap-0.5 transition-opacity duration-150",
                        // Always visible where there is no hover (touch); a
                        // hover reveal on wider screens keeps rows quiet.
                        "sm:opacity-0 sm:group-focus-within:opacity-100 sm:group-hover:opacity-100",
                      )}
                    >
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-10 w-10 text-grey5 hover:text-white sm:h-8 sm:w-8"
                              aria-label={translate("DATA_ROOM/DOWNLOAD_ARIA", {
                                name: file.fileName,
                              })}
                              onClick={(event) => {
                                event.stopPropagation()
                                onDownloadFile(file)
                              }}
                            >
                              <IconDownload size={16} />
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent side="bottom">
                            {translate("DATA_ROOM/DOWNLOAD_TOOLTIP")}
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                      {canDelete && (
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-10 w-10 text-grey5 hover:text-red1 sm:h-8 sm:w-8"
                                aria-label={translate("DATA_ROOM/DELETE_ARIA", {
                                  name: file.fileName,
                                })}
                                onClick={(event) => {
                                  event.stopPropagation()
                                  onDeleteFile(file)
                                }}
                              >
                                <IconTrash size={16} />
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent side="bottom">
                              {translate("DATA_ROOM/DELETE_TOOLTIP")}
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      )}
                    </div>
                  )}
                </div>

                {isEditingNote && (
                  <DataRoomNoteEditor
                    fileName={file.fileName}
                    initialValue={
                      noteDrafts.get(file.id) ?? file.customerNote ?? ""
                    }
                    savedValue={file.customerNote ?? ""}
                    onSave={(note) => onSaveNote(file, note)}
                    onCancel={onCloseNoteEditor}
                    onChange={(value) => onNoteDraftChange(file.id, value)}
                    isSaving={isSavingNote}
                  />
                )}

                {!isEditingNote && file.customerNote && (
                  <SavedNote
                    file={file}
                    editable={canEditNote}
                    onEdit={() => onOpenNoteEditor(file)}
                  />
                )}

                {file.status === "changes_requested" && (
                  <div className="mb-3.5 ml-9 border-l-2 border-warning pl-3">
                    <Typography variant="textXS-bold" className="text-warning">
                      {translate("DATA_ROOM/REVIEWER_NOTE_LABEL")}
                    </Typography>
                    {file.statusComment && (
                      <Typography
                        variant="textXS"
                        className="mt-0.5 text-grey5"
                      >
                        {file.statusComment}
                      </Typography>
                    )}
                    {!file.supersededById && (
                      <div className="mt-2 flex flex-wrap items-center gap-3">
                        <Button
                          variant="outline"
                          size="sm"
                          className="gap-1.5"
                          onClick={() => onUploadRevision(file)}
                          data-testid="data-room-upload-revision"
                        >
                          <IconRefresh size={14} aria-hidden="true" />
                          {translate("DATA_ROOM/REVISION_UPLOAD")}
                        </Button>
                        <Typography variant="textXS" className="text-grey5">
                          {translate("DATA_ROOM/REVISION_HINT")}
                        </Typography>
                      </div>
                    )}
                  </div>
                )}
              </m.li>
            )
          })}
        </ul>
      )}
    </div>
  )
}
